<template>
  <div>
    <div class="header-section">
      <img id="header-background" :src="headerBackground" alt="gradient background" />
      <pvd-title id="header-title">
        <h1 slot="heading" id="header-headline" class="pvd-title__heading pvd-title__heading--level-1">
          {{ header.headline }}
        </h1>
        <span slot="subheading" id="header-teaser" class="pvd-title__heading pvd-title__heading--level-2">
          {{ header.teaser }}
        </span>
        <span slot="subheading" id="header-subtext" v-html="headerSubtext"> </span>
      </pvd-title>
    </div>
    <br />
    <div class="save-section">
      <div id="slider-component-section">
        <h2 id="slider-title" slot="heading" class="pvd-title__heading pvd-title__heading--level-2">
          Lock in the best rate for you
        </h2>
        <div id="education-page-slider-component">
          <fractional-cd-slider
            :id="`apy-slider`"
            :values="apyValuesList"
            :currentValue="sliderIndex"
            :labelSize="`large`"
            @change="sliderChanged($event)"></fractional-cd-slider>
          <pvd-inline-alert id="offerings-alert" v-if="store.offeringsError">{{
            clientConstants.OFFERINGS_UNAVAILABLE_ERROR
          }}</pvd-inline-alert>
        </div>
        <br />
        <div id="get-this-rate-button">
          <pvd-button
            id="amount-modal-trigger"
            pvd-trigger
            :pvd-disabled="this.store.offeringsError"
            @click="openInputModal()"
            ref="amount-modal-trigger">
            {{ clientConstants.GET_THIS_RATE_BUTTON_TEXT }}</pvd-button
          >
          <InputModal
            id="input-modal"
            ref="inputModal"
            :sliderIndex="sliderIndex"
            @modalClosed="closeInputModal($event)"></InputModal>
        </div>
        <br />
        <div id="timestamp-message" v-if="this.store.offeringsLoaded" class="pvd-microcopy-root">
          <p class="pvd-microcopy__microcopy">{{ timestampMessage }}</p>
        </div>
      </div>
      <div class="save-section-info">
        <h2 id="save-section-title" slot="heading" class="pvd-title__heading pvd-title__heading--level-2">
          {{ saveOnYourTermsHeadline }}
        </h2>
        <p id="save-section-text">
          {{ saveOnYourTermsText }}
        </p>
        <pvd-link id="save-info-link" data-cy="view-all-cds" v-html="saveOnYourTermsLink"></pvd-link>
      </div>
    </div>

    <div class="fcd-icons-section">
      <h2 id="fcd-icons-title">
        {{ fcdTitle.headline }}
      </h2>
      <ul class="fcd-icons-list">
        <li v-for="icon in fcdImages" :key="icon.index">
          <span v-html="icon"></span>
        </li>
      </ul>
    </div>

    <div class="risk-benefit-section">
      <div class="learn-more-subsection">
        <h2 id="learn-more-heading">{{ learnMoreText }}</h2>
        <pvd-link id="learn-more-url" v-html="learnMoreUrl"></pvd-link>
      </div>
      <section class="pvd-tile-root">
        <div class="pvd-tile__tile">
          <div class="pvd-tile__content">
            <pvd-expand-collapse pvd-size="large">
              <span slot="heading" class="pvd-expand-collapse__heading" data-cy="benefits-heading">{{
                benefits.subHeadline
              }}</span>
              <div class="pvd-expand-collapse__content" v-html="benefits.text" data-cy="benefits-text"></div>
            </pvd-expand-collapse>
          </div>
        </div>
      </section>

      <section class="pvd-tile-root">
        <div class="pvd-tile__tile">
          <div class="pvd-tile__content">
            <pvd-expand-collapse pvd-size="large">
              <span slot="heading" class="pvd-expand-collapse__heading" data-cy="risks-heading">{{
                risks.subHeadline
              }}</span>
              <div class="pvd-expand-collapse__content" v-html="risks.text" data-cy="risks-text"></div>
            </pvd-expand-collapse>
          </div>
        </div>
      </section>
    </div>

    <div class="faq-section">
      <pvd-title>
        <h2 slot="heading" class="pvd-title__heading pvd-title__heading--level-3" data-cy="faq-title">FAQs</h2>
      </pvd-title>
      <ul class="faq-list">
        <li class="faq-list-item">
          <pvd-expand-collapse pvd-size="medium" v-for="faq in faqList" :key="faq.index">
            <span slot="heading" class="pvd-expand-collapse__heading" v-html="updateTooltip(faq.question)"></span>
            <div class="pvd-expand-collapse__content" v-html="updateTooltip(faq.text)"></div>
          </pvd-expand-collapse>
        </li>
      </ul>
    </div>

    <div class="additional-links-section">
      <h2 id="additional-links-title">
        {{ resources.keyTakeaways }}
      </h2>
      <ul class="additional-links-list">
        <li v-for="link in additionalLinks" :key="link.index">
          <span v-html="link"></span>
        </li>
      </ul>
      <div class="additional-links-button">
        <pvd-button pvd-id="additional-links-cd-button" :pvd-href="cdLaddersUrl">Get started buying a CD</pvd-button>
      </div>
      <div class="additional-links-button">
        <pvd-button
          pvd-variant="secondary"
          pvd-id="additional-links-account-button"
          :pvd-href="clientConstants.OPEN_ACCOUNT_URL"
          >Open an account</pvd-button
        >
      </div>
    </div>

    <div class="disclosures-section">
      <br />
      <pvd-footnotes>
        <pvd-footnote v-for="disclosure in disclosures" :key="disclosure.text">
          <pvd-microcopy v-html="disclosure.text" data-cy="disclosure"></pvd-microcopy>
          <br />
        </pvd-footnote>
      </pvd-footnotes>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive } from '@vue/runtime-core';
import { ClientConstants, TCM_MAPPING, TOOLTIP_MAPPINGS } from './../client.constants';
import {
  ContentBodySectionModel,
  ContentComponentsModel,
  ContentDetailModel,
  ContentDisclosuresModel
} from './../models/content-model';
import { FractionalCDModel } from './../models/fractional-cd-model';
import FractionalCdSlider from './FractionalCdSlider.vue';
import store from './../store/store';
import { Utils } from './../utils/Utils';
import InputModal from './InputModal.vue';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let app: any;

export default defineComponent({
  name: 'ContentComponent',
  components: { FractionalCdSlider, InputModal },
  computed: {
    headerBackground() {
      switch (this.windowWidth > 0) {
        case this.windowWidth > 960:
          return this.headerBackgroundL;
        case this.windowWidth >= 768:
          return this.headerBackgroundM;
        case this.windowWidth >= 375:
          return this.headerBackgroundS;
        default:
          return this.headerBackgroundS;
      }
    },
    contentComponents() {
      return store.contentItems[0]?.components ?? [];
    },
    faqList() {
      const faqComponent = Utils.mapContent(this.contentComponents, TCM_MAPPING.FAQS) || {};
      return faqComponent?.components?.map((component: ContentDetailModel) => component.contentDetail) ?? [];
    },
    apyValuesList() {
      return store.offeringsModel.offerings.map((offering: FractionalCDModel, key: number) => {
        return {
          key: key,
          leftValue: offering.termToMaturity,
          rightValue: offering.sliderRate
        };
      });
    },
    timestampMessage() {
      const timestamp = Utils.formatTimestamp(store.offeringsModel.timestamp);
      return timestamp == '' ? '' : this.clientConstants.TIMESTAMP_MESSAGE.replace('{{ timestamp }}', timestamp);
    }
  },
  data: () => {
    return {
      clientConstants: ClientConstants,
      header: {} as ContentDetailModel,
      windowWidth: window.innerWidth,
      headerBackgroundS: '',
      headerBackgroundM: '',
      headerBackgroundL: '',
      headerSubtext: '',
      fcdTitle: {} as ContentDetailModel,
      fcdImages: [] as string[],
      saveOnYourTermsHeadline: '',
      saveOnYourTermsText: '',
      saveOnYourTermsLink: '',
      sliderIndex: 3,
      store: reactive(store),
      learnMoreText: '',
      learnMoreUrl: '',
      benefits: {} as ContentBodySectionModel,
      risks: {} as ContentBodySectionModel,
      resources: {} as ContentDetailModel,
      additionalLinks: [] as string[],
      cdLaddersUrl: '',
      disclosures: [] as ContentDisclosuresModel[]
    };
  },
  mounted() {
    // Why is this in a next tick?
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize);
    });

    this.setHeaderContent();
    this.setSaveOnYourTermsContent();
    this.setIconsContent();
    this.setBenefitsRisksContent();
    this.setAddlResourcesContent();
    this.setDisclosuresContent();

    try {
      Utils.trackingFunction(this.clientConstants.EDU_PAGE);
    } catch (err) {
      console.log('Error In track analytics ' + err);
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    sliderChanged(event: Event) {
      this.sliderIndex = Number((event.target as HTMLInputElement).value);
    },
    setHeaderContent() {
      this.header = Utils.mapContent(this.contentComponents, TCM_MAPPING.HEADER_INFO).contentDetail || {};
      this.headerBackgroundS = Utils.processHost(this.header.smallImage?.path, app?.wwwHost);
      this.headerBackgroundM = Utils.processHost(this.header.mediumImage?.path, app?.wwwHost);
      this.headerBackgroundL = Utils.processHost(this.header.largeImage?.path, app?.wwwHost);
      this.headerSubtext = this.header.bodySection?.[0].text ?? '';
    },
    setSaveOnYourTermsContent() {
      const saveOnYourTerms = Utils.mapContent(this.contentComponents, TCM_MAPPING.SAVE).contentDetail || {};
      this.saveOnYourTermsHeadline = saveOnYourTerms?.bodySection?.[0].headline || '';
      this.saveOnYourTermsText = saveOnYourTerms?.bodySection?.[0].text || '';
      this.saveOnYourTermsLink = saveOnYourTerms?.bodySection?.[1].text || '';
    },
    setIconsContent() {
      this.fcdTitle = Utils.mapContent(this.contentComponents, TCM_MAPPING.FCD_ICONS).contentDetail || {};
      this.fcdImages =
        this.fcdTitle.bodySection
          ?.map((icon: ContentBodySectionModel) => {
            return Utils.processImgTag(icon.text, app?.hostUrl);
          })
          .slice(0, 4) ?? [];
    },
    setBenefitsRisksContent() {
      const benefitsRisks = Utils.mapContent(this.contentComponents, TCM_MAPPING.BENEFITS_RISKS).contentDetail || {};
      this.learnMoreText = benefitsRisks?.headline || '';
      this.learnMoreUrl = benefitsRisks?.teaser || '';
      this.benefits = benefitsRisks?.bodySection?.[0] || {};
      this.risks = benefitsRisks?.bodySection?.[1] || {};
    },
    setAddlResourcesContent() {
      this.resources = Utils.mapContent(this.contentComponents, TCM_MAPPING.ADDL_RESOURCES).contentDetail || {};
      this.additionalLinks =
        this.resources.bodySection
          ?.map((link: ContentBodySectionModel) => {
            return Utils.processImgTag(link.text, app?.hostUrl);
          })
          .slice(0, 4) ?? [];
      this.cdLaddersUrl = `${app?.fixedIncomeHost}${app?.fiLandingCdLadders}`;
    },
    setDisclosuresContent() {
      this.disclosures =
        Utils.mapContent(this.contentComponents, TCM_MAPPING.DISCLOSURES).contentDetail?.disclosures || [];
    },
    onResize() {
      this.windowWidth = window.innerWidth;
    },
    openInputModal() {
      (this.$refs['inputModal'] as typeof InputModal).openModal();
    },
    closeInputModal(event: { amount: string; index: number }) {
      this.navigateToScreener(event.amount, event.index);
    },
    navigateToScreener(amount: string, index: number) {
      this.$router.push(`/screener?amount=${Utils.formatNumber(amount)}&duration=${index}`);
    },
    fixScrollbar() {
      // Clearing body style completely puts back the scroll bar when navigating to the screener.
      // However, this is a heavy-handed approach.
      document.body.setAttribute('style', '');
    },
    updateTooltip(content: string) {
      let updatedContent = content;
      TOOLTIP_MAPPINGS.forEach(mapping => {
        const match = this.store.contentItems[0]?.components.find(
          (component: ContentComponentsModel) => component.sourceId === mapping.id
        );
        updatedContent = Utils.fillInTooltip(
          updatedContent,
          mapping.key,
          match?.contentDetail?.headline,
          match?.contentDetail?.text
        );
      });
      return updatedContent;
    }
  },
  watch: {
    $route(to, from) {
      if (to.path === '/screener' && from.path === '/') {
        this.fixScrollbar();
      }
    }
  }
});
</script>

<style scoped lang="scss">
@import '@fmr-pr101742/providence-visual-style/styles/visual-style.scss';
.header-section {
  position: relative;
  max-height: 23rem;
  display: flex;
  flex-direction: column;

  @media only screen and (min-width: $desktop-mid) and (max-width: $desktop-max) {
    max-height: 25rem;
  }
  @media only screen and (min-width: $tablet-max) and (max-width: $desktop-mid) {
    max-height: 28rem;
  }
  @media only screen and (min-width: $tablet-small) and (max-width: $tablet-max) {
    max-height: 32rem;
  }
  @media only screen and (min-width: $mobile-max) and (max-width: $tablet-small) {
    max-height: 34rem;
  }
  @media only screen and (min-width: $mobile-small) and (max-width: $mobile-max) {
    max-height: 35rem;
  }
  @media only screen and (max-width: $mobile-small) {
    max-height: 40rem;
  }
}

#header-background {
  width: 100%;
  height: 23rem;

  @media only screen and (min-width: $desktop-mid) and (max-width: $desktop-max) {
    height: 25rem;
  }
  @media only screen and (min-width: $tablet-max) and (max-width: $desktop-mid) {
    height: 28rem;
  }
  @media only screen and (min-width: $tablet-small) and (max-width: $tablet-max) {
    height: 32rem;
  }
  @media only screen and (min-width: $mobile-max) and (max-width: $tablet-small) {
    height: 34rem;
  }
  @media only screen and (min-width: $mobile-small) and (max-width: $mobile-max) {
    height: 35rem;
  }
  @media only screen and (max-width: $mobile-small) {
    height: 40rem;
  }
}

#header-title {
  position: absolute;
  padding-top: 4rem;
  padding-left: 3rem;
  @media only screen and (max-width: $mobile-max) {
    padding-top: 2rem;
  }
}

#header-headline,
#header-teaser {
  color: $pvd-color-palette-white;
  display: block;
  font-weight: $pvd-font-weight-bold;
}

#header-headline {
  font-size: 1.25rem;
}

#header-teaser {
  font-size: 2.5rem;
  @media only screen and (min-width: $mobile-extra-small) and (max-width: $mobile-max) {
    font-size: 2.25rem;
  }
  @media only screen and (max-width: $mobile-extra-small) {
    font-size: 2rem;
  }
}

#header-subtext {
  display: block;
  text-align: left;
  padding-top: 2rem;
  width: 70%;
  font-size: 1.625rem;
  color: $pvd-color-palette-white;

  @media only screen and (min-width: $desktop-mid) and (max-width: $desktop-max) {
    width: 80%;
  }
  @media only screen and (min-width: $tablet-max) and (max-width: $desktop-mid) {
    width: 85%;
  }
  @media only screen and (min-width: $desktop-mid) and (max-width: $desktop-large) {
    width: 85%;
  }
  @media only screen and (min-width: $mobile-max) and (max-width: $tablet-max) {
    width: 90%;
  }
  @media only screen and (max-width: $mobile-max) {
    padding-top: 1.5rem;
    font-size: 1.375rem;
    width: 95%;
  }
}

.save-section {
  display: flex;
  justify-content: space-around;
  padding-top: $pvd-space-inset-two-x;
  padding-bottom: $pvd-space-inset-two-x;
  text-align: left;

  @media only screen and (max-width: $desktop-mid) {
    gap: $pvd-space-inset-two-x;
    flex-wrap: wrap;
  }

  #slider-component-section {
    width: 35%;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;

    @media only screen and (min-width: $desktop-mid) and (max-width: $desktop-max) {
      width: 33%;
    }
    @media only screen and (min-width: $tablet-max) and (max-width: $desktop-mid) {
      width: 90%;
    }
    @media only screen and (min-width: $mobile-max) and (max-width: $tablet-max) {
      width: 85%;
    }
    @media only screen and (max-width: $mobile-max) {
      width: 80%;
    }
    @media only screen and (min-width: 512px) and (max-width: 768px) {
      width: 88%;
    }
  }

  #slider-title {
    @include pvd-heading-level-3;
  }

  #education-page-slider-component {
    width: 100%;
    @media only screen and (min-width: $tablet-max) and (max-width: $desktop-mid) {
      width: 40%;
    }
    @media only screen and (min-width: $mobile-max) and (max-width: $tablet-max) {
      width: 75%;
    }
    @media only screen and (max-width: $mobile-max) {
      width: 82%;
    }
    @media only screen and (max-width: $mobile-small) {
      width: 70%;
    }
  }

  .pvd-microcopy__microcopy {
    padding-bottom: $pvd-space-inset-one-x;
  }

  #get-this-rate-button {
    display: flex;
  }

  .save-section-info {
    width: 50%;

    @media only screen and (min-width: $tablet-max) and (max-width: $desktop-mid) {
      width: 90%;
    }
    @media only screen and (min-width: $mobile-max) and (max-width: $tablet-max) {
      width: 85%;
    }
    @media only screen and (max-width: $mobile-max) {
      width: 80%;
    }
    @media only screen and (min-width: 512px) and (max-width: 768px) {
      width: 88%;
    }
  }

  #save-section-title {
    @include pvd-heading-level-3;
  }

  #save-section-text {
    @include pvd-text-medium();
  }
}

.risk-benefit-section {
  padding: 0 $pvd-space-inset-four-x $pvd-space-inset-two-x $pvd-space-inset-four-x;
  .pvd-tile-root {
    padding-bottom: $pvd-space-inset-one-x;
  }

  .pvd-expand-collapse__content > p {
    margin-bottom: 0;
  }
}

.learn-more-subsection {
  text-align: left;
  padding-bottom: $pvd-space-inset-one-x;
}

#learn-more-heading {
  @include pvd-heading-level-3();
  padding-bottom: $pvd-space-inset-one-x;
}

.faq-section {
  text-align: left;
  padding: 0 $pvd-space-inset-four-x;
}

.faq-list {
  list-style: none;
  padding-left: 0;
}

.faq-list-item {
  padding: 0 0 $pvd-space-inset-one-x 0;

  pvd-expand-collapse {
    padding-bottom: $pvd-space-inset-one-and-half-x;
  }
}

.fcd-icons-section,
.additional-links-section {
  text-align: center;
  padding: $pvd-space-inset-two-x $pvd-space-inset-four-x;

  @media only screen and (max-width: $mobile-max) {
    padding: $pvd-space-inset-two-x;
  }

  #fcd-icons-title,
  #additional-links-title {
    @include pvd-heading-level-3();
    text-align: center;
    padding-bottom: $pvd-space-inset-one-x;

    @media only screen and (max-width: $tablet-max) {
      text-align: center;
    }
  }

  .fcd-icons-list,
  .additional-links-list {
    list-style: none;
    display: flex;
    justify-content: space-around;
    padding-bottom: $pvd-space-inset-two-x;
    padding-left: 0;

    @media only screen and (min-width: $tablet-max) and (max-width: $desktop-large) {
      padding-bottom: $pvd-space-inset-one-x;
    }
    @media only screen and (max-width: $tablet-max) {
      flex-wrap: wrap;
      padding-bottom: $pvd-space-inset-one-x;
    }

    li {
      width: 20%;

      @media only screen and (min-width: $mobile-max) and (max-width: $tablet-max) {
        width: 35%;
        padding-bottom: $pvd-space-inset-one-x;
      }
      @media only screen and (max-width: $mobile-max) {
        width: 70%;
        padding-bottom: $pvd-space-inset-one-x;
      }
    }
  }

  .additional-links-button {
    margin: auto;
    padding: $pvd-space-inset-half-x 0;
  }
}

.disclosures-section {
  text-align: left;
  padding: 0 $pvd-space-inset-four-x;
}
</style>
